/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

// Component imports
import { Alert, Card, Col, Collapse, Form, Input, Row, Select, Skeleton, Space, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';
import { ArrowRightIcon } from '../../../Icons/ArrowRightIcon';

// Misc imports
import is from '../../../../utils/is';
import replaceUrlParams from '../../../../utils/string/replaceUrlParams';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../../../../constants/main';
import { NAV_BOT, NAV_ORGANIZATION_USERS } from '../../../../constants/navigation';
import { NODE_TYPES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { useFetchUserGroupsQuery } from '../../../../store/userGroups/userGroupsApi';
import { isJson } from '../../../../utils/is/isJson';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, INTEGRATIONS } from '../../../../constants/integrations';

// Constant declarations
const { Text } = Typography;
const { Option } = Select;

const LiveChatRequestNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const { isEditingPrimaryLanguage } = useBotLanguage();

  const { currentUser } = useSelector((state) => state.auth);
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: userGroups, isFetching: isFetchingUserGroups } = useFetchUserGroupsQuery({ orgId, urlParams: { pageSize: 100 } }, { skip: selectedElement.type !== NODE_TYPES.LIVECHAT_REQUEST });

  const userGroupsWithLiveAgentRole = userGroups && is.array(userGroups.items) ? userGroups.items.filter(g => g.organizationRoles.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT)) : [];
  const isOrgAdmin = currentUser?.effectiveRoles.organizationRoles?.[orgId]?.includes(PERMISSIONS_ORG.ADMIN);
  const livechatPlugin = bot?.plugins?.find(plugin => plugin.integration.category === INTEGRATION_CATEGORY.LIVECHAT);

  const [isGroupDeleted, setIsGroupDeleted] = useState(false);

  useEffect(() => {
    if (selectedElement?.type === NODE_TYPES.LIVECHAT) {
      if (selectedElement.data.livechatGroup && userGroupsWithLiveAgentRole.length > 0) {
        const groupExists = userGroupsWithLiveAgentRole.find(userGroup => userGroup.id === selectedElement.data.livechatGroup);
        !groupExists ? setIsGroupDeleted(true) : setIsGroupDeleted(false);
      } else {
        setIsGroupDeleted(false);
      }
    }
  }, [selectedElement, userGroupsWithLiveAgentRole]);

  const renderCurrentLivechatPlugin = () => {
    if (!livechatPlugin) {
      return (
        <Row>
          <Col span={24} className="mb-4">
            <Alert
              message={
                <Trans t={t}>
                  {t('No LiveChat plugin is enabled for this agent. Click')} <strong>
                    <Link
                      to={replaceUrlParams(NAV_BOT, { tenantId: bot.id, orgId }) + '?view=plugins'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('here')}
                    </Link>
                  </strong> {t('to enable one.')}
                </Trans>
              }
              type="warning"
              showIcon
            />
          </Col>
        </Row>
      );
    } else {
      const integrationDetails = Object.values(INTEGRATIONS).find(integration => integration.type === livechatPlugin.integration.type);

      return (
        <Form.Item
          label={t('Active LiveChat Plugin')}
          className="mb-4"
        >
          <Row>
            <Col>
              <Card size="small" className="h-100 shadow-none" bodyStyle={{ height: '100%' }}>
                <Space direction="horizontal" size="middle" className="px-1">
                  {integrationDetails.renderTypeIcon({ className: 'fs-xxxl' })}
                  {t(integrationDetails.type, { context: 'type' })}
                </Space>
              </Card>
            </Col>
          </Row>
        </Form.Item>
      );
    }
  };

  return (
    <Fragment>
      {renderCurrentLivechatPlugin()}

      {
        livechatPlugin?.integration?.type === INTEGRATION_TYPE.HELVIA_LIVECHAT ? (
          <Fragment>
            <Collapse
              className="mb-4"
              bordered={false}
              expandIcon={({ isActive }) => isActive ? <ArrowDownIcon /> : <ArrowRightIcon />}
            >
              <Collapse.Panel
                header={t('Helvia LiveChat additional settings')}
                forceRender
              >
                <Form.Item
                  name={['data', 'livechatGroup']}
                  className="mb-0"
                  label={
                    <Fragment>
                      <Typography.Text>
                        {t('Assign to User Group')}
                        <Tooltip
                          className="ml-1"
                          placement="top"
                          title= {t('You can optionally select a User Group with the Live Agent role enabled, to assign LiveChat requests to. Only members of the selected group will be able to accept the requests.')}
                        >
                          <QuestionCircleOutlined className="fs-sm position-absolute" />
                        </Tooltip>
                      </Typography.Text>
                    </Fragment>
                  }
                >
                  {
                    isFetchingUserGroups && is.emptyArray(userGroupsWithLiveAgentRole) ? (
                      <Skeleton.Input active size="large" className="w-100 opacity-70 border-radius-base" />
                    ) : (
                      <Select
                        status={isGroupDeleted ? 'error' : ''}
                        placeholder={t('Select a group')}
                        size="large"
                        showSearch
                        allowClear
                        disabled={(isFetchingUserGroups && !userGroupsWithLiveAgentRole) || !isEditingPrimaryLanguage}
                        loading={isFetchingUserGroups}
                        filterOption={(input, option) => userGroupsWithLiveAgentRole.find(userGroup => userGroup.id === option.value)?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        onChange={() => setIsGroupDeleted(false)}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                      >
                        {
                          userGroupsWithLiveAgentRole
                            .slice() // Avoid mutating original array
                            .sort((a, b) => a.name && b.name && a.name.localeCompare(b.name))
                            .map(userGroup => (
                              <Option key={userGroup.id} value={userGroup.id}>{userGroup.name}</Option>
                            ))
                        }
                      </Select>
                    )
                  }
                </Form.Item>

                {
                  isGroupDeleted ? (
                    <Row>
                      <Col span={24} className="my-3">
                        <Text className="text-error fs-lg">
                          {t('The selected User Group with ID')} {selectedElement.data.livechatGroup} {t('has been deleted and requests are sent to all Live Agents of your organization.')}
                          {
                            !is.emptyArray(userGroupsWithLiveAgentRole) ? (
                              <Text className="text-error ml-1">
                                {t('Select another group to direct LiveChat requests to specific users.')}
                              </Text>
                            ) : null
                          }
                        </Text>
                      </Col>
                    </Row>
                  ) : null
                }

                {
                  is.emptyArray(userGroupsWithLiveAgentRole) ? (
                    <Row>
                      <Col span={22} className="my-3">
                        <Space direction="horizontal" align="baseline">
                          <Typography.Text className="fs-base text-warning">
                            <WarningOutlined />
                          </Typography.Text>

                          <Space direction="vertical">
                            <Text>
                              {t('There are no LiveChat groups available in your organisation.')}
                            </Text>

                            {
                              isOrgAdmin ? (
                                <Text>
                                  {t(`Go to Organization's`)}
                                  <a
                                    href={(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?tab=user-groups`)}
                                    className="mx-1"
                                  >
                                    {t('User Groups')}
                                  </a>
                                  {t('to create one.')}
                                </Text>
                              ) : null
                            }
                          </Space>
                        </Space>
                      </Col>
                    </Row>
                  ) : null
                }
              </Collapse.Panel>
            </Collapse>
          </Fragment>
        ) : null
      }

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => isActive ? <ArrowDownIcon /> : <ArrowRightIcon />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <Form.Item
            label={
              <Fragment>
                <Typography.Text>
                  {t('Metadata')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('Define payload with custom metadata upon request.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Typography.Text>
              </Fragment>
            }
            validateFirst
            name={['data', 'requestExtras']}
            hasFeedback
            validateTrigger="onChange"
            trigger="onChange"
            shouldUpdate
            className="mb-0"
            rules={[
              {
                required: false,
                validator: (rule, value) => {
                  if ((!isJson(value))) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input.TextArea
              placeholder="Custom payload in JSON format"
              autoSize={{ minRows: 6, maxRows: 32 }}
            />
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

LiveChatRequestNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default LiveChatRequestNodeForm;
